import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { DipendentiActions } from './dipendenti.actions';
import { DipendentiPort } from '../../core/services/dipendenti/dipendenti.port';
import { catchError, map, of, switchMap, tap, finalize } from 'rxjs';
import { LoadingService } from '../../core/services/loading/loading.service';

@Injectable()
export class DipendentiEffects {
  private actions$ = inject(Actions);
  private api = inject(DipendentiPort);
  private loading = inject(LoadingService);

  loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DipendentiActions.loadList),
      switchMap(() =>
        this.api.getListPaginated({}).pipe(
          map((res) => DipendentiActions.loadListSuccess({ items: res.content, totalElements: res.totalElements, totalPages: res.totalPages, pageIndex: res.pageIndex })),
          catchError((err) =>
            of(DipendentiActions.loadListFailure({ error: err?.message ?? 'Errore caricamento' }))
          )
        )
      )
    )
  );

  loadListPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DipendentiActions.loadListPaginated),
      switchMap(({ params }) =>
        this.api.getListPaginated(params ?? {}).pipe(
          map((res) => DipendentiActions.loadListPaginatedSuccess({ items: res.content, totalElements: res.totalElements, totalPages: res.totalPages, pageIndex: res.pageIndex })),
          catchError((err) =>
            of(DipendentiActions.loadListPaginatedFailure({ error: err?.message ?? 'Errore caricamento' }))
          )
        )
      )
    )
  );

  loadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DipendentiActions.loadDetails),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.getById(id).pipe(
          map((item) => DipendentiActions.loadDetailsSuccess({ item })),
          catchError((err) =>
            of(DipendentiActions.loadDetailsFailure({ error: err?.message ?? 'Errore dettagli' }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DipendentiActions.create),
      tap(() => this.loading.show()),
      switchMap(({ dto }) =>
        this.api.create(dto).pipe(
          map(() => DipendentiActions.createSuccess()),
          catchError((err) =>
            of(DipendentiActions.createFailure({ error: err?.message ?? 'Errore creazione' }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  reloadAfterCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DipendentiActions.createSuccess),
      map(() => DipendentiActions.loadList())
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DipendentiActions.update),
      tap(() => this.loading.show()),
      switchMap(({ id, dto }) =>
        this.api.update(id, dto).pipe(
          map(() => DipendentiActions.updateSuccess()),
          catchError(err =>
            of(DipendentiActions.updateFailure({
              error: err?.message ?? 'Errore modifica dipendente',
            }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  archive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DipendentiActions.archive),
      tap(() => this.loading.show()),
      switchMap(({ id, archive }) =>
        this.api.archive(id, archive).pipe(
          map(() => DipendentiActions.archiveSuccess()),
          catchError(err =>
            of(DipendentiActions.archiveFailure({
              error: err?.message ?? 'Errore archiviazione dipendente',
            }))
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  reloadAfterUpdateOrArchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DipendentiActions.updateSuccess,
        DipendentiActions.archiveSuccess
      ),
      map(() => DipendentiActions.loadList())
    )
  );

  loadDatiAzienda$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DipendentiActions.loadDatiAzienda),
      switchMap(({ id }) =>
        this.api.getDatiAzienda(id).pipe(
          map(item => DipendentiActions.loadDatiAziendaSuccess({ item })),
          catchError(err =>
            of(DipendentiActions.loadDatiAziendaFailure({
              error: err?.message ?? 'Errore caricamento dati azienda',
            }))
          )
        )
      )
    )
  );

  updateDatiAzienda$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DipendentiActions.updateDatiAzienda),
      switchMap(({ id, dto }) =>
        this.api.updateDatiAzienda(id, dto).pipe(
          map(() => DipendentiActions.updateDatiAziendaSuccess({ id })),
          catchError(err =>
            of(DipendentiActions.updateDatiAziendaFailure({
              error: err?.message ?? 'Errore modifica dati azienda',
            }))
          )
        )
      )
    )
  );

  reloadAfterUpdateDatiAzienda$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DipendentiActions.updateDatiAziendaSuccess),
      map(({ id }) => DipendentiActions.loadDatiAzienda({ id }))
    )
  );

  uploadLogoDatiAzienda$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DipendentiActions.uploadLogoDatiAzienda),
      switchMap(({ id, file }) =>
        this.api.uploadLogoDatiAzienda(id, file).pipe(
          map(() => DipendentiActions.uploadLogoDatiAziendaSuccess({ id })),
          catchError(err =>
            of(DipendentiActions.uploadLogoDatiAziendaFailure({
              error: err?.message ?? 'Errore upload logo',
            }))
          )
        )
      )
    )
  );

  reloadAfterUploadLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DipendentiActions.uploadLogoDatiAziendaSuccess),
      map(({ id }) => DipendentiActions.loadDatiAzienda({ id }))
    )
  );
}
